import { SchoolInfo } from "@/services/school";
import provinceList from "@province-city-china/province";
import cityList from "@province-city-china/city";
import areaList from "@province-city-china/area";
import { KUAIPK_TIPS } from "@/config/constants";
import { message } from "antd";

export const isObj = (data: any) => {
  return typeof data === "object" && data !== null && !Array.isArray(data);
};

export const clearUndefined = (data: Record<string, unknown>) => {
  Object.keys(data).forEach((key) => {
    if (data[key] === undefined) {
      Reflect.deleteProperty(data, key);
    }
  });
  return data;
};

// 使用文件流下载（企微不支持）
export function downloadFileStream(fileStream: Blob | string, filename: string, type?: string) {
  const blob = new Blob([fileStream], { type: type || "" });
  const a = document.createElement("a");
  a.download = filename;
  a.style.display = "none";
  a.href = URL.createObjectURL(blob);
  document.body.appendChild(a);
  a.click();
  URL.revokeObjectURL(a.href);
  document.body.removeChild(a);
}

// 使用url下载（会跳转/打开，非直接下载）
export function downloadFileUrl(url: string, target?: "_self" | "_blank") {
  const a = document.createElement("a");
  a.href = url;
  a.target = target || "_self";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/**
 * 从 URL 直接下载文件（fetch 获取 blob 后触发下载，不跳转）
 * 若因 CORS 等原因 fetch 失败，会 fallback 为新标签页打开
 * @param url 文件地址
 * @param filename 下载时的文件名，默认从 URL 提取或使用 "download"
 * @returns true=直接下载成功, false=fallback 为新标签页打开
 */
export async function downloadFileFromUrl(url: string, filename?: string): Promise<boolean> {
  try {
    const response = await fetch(url, { mode: "cors" });
    if (!response.ok) throw new Error("下载失败");
    const blob = await response.blob();
    const name =
      filename ||
      decodeURIComponent(url.split("/").pop() || "").split("?")[0] ||
      `invoice_${Date.now()}.png`;
    downloadFileStream(blob, name);
    return true;
  } catch {
    // CORS 或网络问题导致 fetch 失败时，fallback 为新标签页打开（用户可手动保存）
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    return false;
  }
}

/**
 * 从 Content-Disposition 解析下载名；失败则回退 .xlsx，避免落成 .txt。
 */
export function resolveXlsxDownloadFilename(
  contentDisposition: string | null | undefined,
  fallback: string,
): string {
  const fb = (fallback || "download.xlsx").trim();
  const safeFallback = /\.xlsx?$/i.test(fb) ? fb : `${fb}.xlsx`;
  if (!contentDisposition) return safeFallback;

  const quoted = /filename="([^"]+)"/i.exec(contentDisposition);
  if (quoted?.[1]?.trim()) return quoted[1].trim();

  const star = /filename\*\s*=\s*UTF-8''([^;\s]+)/i.exec(contentDisposition);
  if (star?.[1]) {
    try {
      const decoded = decodeURIComponent(star[1].trim());
      if (decoded && !/^UTF-8''/i.test(decoded)) return decoded;
    } catch {
      /* ignore */
    }
  }

  const plain = /filename\s*=\s*([^;]+)/i.exec(contentDisposition);
  if (plain?.[1]) {
    const raw = plain[1].trim().replace(/^["']|["']$/g, "");
    if (raw && !/^UTF-8''/i.test(raw)) return raw;
  }
  return safeFallback;
}

export async function downloadResponseHandle(
  response: Response,
  fallbackFilename = "download.xlsx",
) {
  const blob = await response.blob();

  // json表示有异常
  if (blob.type.includes("application/json")) {
    try {
      return JSON.parse(await blob.text());
    } catch {
      return {
        code: response.ok ? 500 : response.status,
        msg: "文件下载失败，请稍后重试",
      };
    }
  }

  const filename = resolveXlsxDownloadFilename(
    response.headers.get("Content-Disposition"),
    fallbackFilename,
  );
  downloadFileStream(blob, filename.replace(/["']/g, ""));

  return {
    code: 200,
  };
}

export const numberToDay: Record<string | number, string> = {
  1: "星期一",
  2: "星期二",
  3: "星期三",
  4: "星期四",
  5: "星期五",
  6: "星期六",
  7: "星期日",
};

export const numberToDaySimple: Record<string | number, string> = {
  1: "一",
  2: "二",
  3: "三",
  4: "四",
  5: "五",
  6: "六",
  7: "日",
};

export const numberToGrade: Record<string | number, string> = {
  1: "一年级",
  2: "二年级",
  3: "三年级",
  4: "四年级",
  5: "五年级",
  6: "六年级",
  7: "七年级",
  8: "八年级",
  9: "九年级",
};

export const gradeToNumber: Record<string, number> = {
  一年级: 1,
  二年级: 2,
  三年级: 3,
  四年级: 4,
  五年级: 5,
  六年级: 6,
  七年级: 7,
  八年级: 8,
  九年级: 9,
};

// 获取表格行合并数据
export const getTableCellCollapsedData = (
  dataIndex: string,
  list: any[] = [],
  spanKey?: string,
) => {
  let cellSpanKey = spanKey || "rowSpan";
  let timesCountMap = new Map();
  list.forEach((item) => {
    if (!timesCountMap.has(item[dataIndex])) {
      timesCountMap.set(item[dataIndex], 1);
    } else {
      timesCountMap.set(item[dataIndex], timesCountMap.get(item[dataIndex]) + 1);
    }
  });
  const data: any[] = [];
  [...timesCountMap].forEach((entry) => {
    const count = entry[1];
    for (let i = 0; i < count; i++) {
      if (i === 0) {
        data.push({
          [cellSpanKey]: count,
        });
      } else {
        data.push({
          [cellSpanKey]: 0,
        });
      }
    }
  });
  return data;
};

export const uniqArr = (arr: any[]) => {
  return [...new Set(arr)];
};

const chineseNumMap = new Map([
  ["一", 1],
  ["二", 2],
  ["三", 3],
  ["四", 4],
  ["五", 5],
  ["六", 6],
  ["七", 7],
  ["八", 8],
  ["九", 9],
]);

export const chineseNumToNum = (chineseNum: string) => {
  return chineseNumMap.get(chineseNum) || 0;
};

export const getSchoolLocationName = (school?: SchoolInfo) => {
  if (!school) {
    return "";
  }
  const provinceName = provinceList.find((item) => school.provinceCode === item.code)?.name;
  const cityName = cityList.find((item) => school.cityCode === item.code)?.name;
  const areaName = areaList.find((item) => school.areaCode === item.code)?.name;

  return `${provinceName || ""}${cityName || ""}${areaName || ""}`;
};

export const isBase64 = (str: string) => {
  if (str.trim() === "") {
    return false;
  }
  try {
    return btoa(atob(str)) === str;
  } catch (e) {
    return false;
  }
};

export const getIsWxBrowser = () => {
  const ua = navigator.userAgent.toLowerCase();
  // 微信浏览器并且非企业微信浏览器
  return ua.indexOf("micromessenger") !== -1 && ua.indexOf("wxwork") === -1;
};

export const ellipsisText = (str: string, maxLength: number) => {
  if (str.length <= maxLength) {
    return str;
  }
  return str.substring(0, maxLength) + "...";
};

export const mobileReg = /^1[3-9]\d{9}$/;

export const wrapDownloadMessageLoading = (promiseFn?: () => Promise<any>) => {
  if (!promiseFn) {
    return promiseFn;
  }
  return async () => {
    let close;
    try {
      close = message.loading({ content: "正在下载文件..." });
      await promiseFn();
    } finally {
      close?.();
    }
  };
};

export const getKuaipkTip = (key: string) => {
  return JSON.parse(localStorage.getItem(KUAIPK_TIPS) || "{}")[key];
};

export const setKuaipkTip = (key: string, value: any) => {
  const tips = JSON.parse(localStorage.getItem(KUAIPK_TIPS) || "{}");
  tips[key] = value;
  localStorage.setItem(KUAIPK_TIPS, JSON.stringify(tips));
};

export function getScrollHeight(element: HTMLDivElement) {
  // 获取元素的内容高度
  const totalHeight = element.offsetHeight;
  // 获取元素的可视区域高度
  const viewportHeight = element.clientHeight;
  // 获取滚动条高度
  const scrollHeight = totalHeight - viewportHeight;
  return scrollHeight;
}

// 换行
export function lineFeedIfMax(str: string, max: number): string {
  const cList = str.split("\n");
  let last = cList.pop() || "";
  if (last.length > max) {
    last = last.slice(0, max) + "\n" + last.slice(max);
  }
  return cList.length ? cList.join("\n") + "\n" + last : last;
}

export const getBackUrl = () => {
  return window.location.href.split("#")[1] || "";
};
